/**
 * Central engine URL configuration.
 *
 * standard-core.md §8/§9 ("Segurança"): no secret or service URL hardcoded —
 * env only, with NO fallback to a production URL. Before this module, nine
 * call sites each carried their own
 * `import.meta.env.VITE_ENGINE_URL || '<the production engine URL>'`
 * fallback. If a deploy ever shipped without `VITE_ENGINE_URL` set (a CI/
 * Vercel misconfiguration), every one of those call sites would silently
 * start talking to the PRODUCTION engine — including from a staging or CEO
 * demo build. That is the exact failure mode this module closes.
 *
 * Rule enforced here: if the required env var is missing,
 *   - in a production build (`import.meta.env.PROD`) this THROWS — a
 *     misconfigured deploy must fail loud, never cross-talk to prod.
 *   - in local dev (`import.meta.env.DEV`) it falls back to an explicit
 *     DEV-only default that points at the STAGING engine (never
 *     production), so `npm run dev` still works without a committed `.env`.
 *
 * This is the only module allowed to hold a literal engine service URL.
 * Every other module must call these getters — see
 * `src/lib/config.test.ts` for the grep gate that enforces it.
 */

/** DEV-only default — staging engine, never production. See module doc. */
const DEV_ENGINE_URL = 'https://axiomgo-engine-staging.up.railway.app'
/** DEV-only default — staging engine WS endpoint, never production. */
const DEV_ENGINE_WS_URL = 'wss://axiomgo-engine-staging.up.railway.app/ws'
/** Local engine dev server, used only when the browser itself is on localhost. */
export const LOCAL_ENGINE_REST_URL = 'http://localhost:4000'
/** Local engine dev server WS endpoint, used only when the browser itself is on localhost. */
export const LOCAL_ENGINE_WS_URL = 'ws://localhost:4000/ws'

/**
 * Os hosts que SÃO produção — a lista que uma funcionalidade consulta para se
 * DESLIGAR quando a tela foi compilada contra a produção.
 *
 * Mora aqui porque este módulo é, por documentação no topo do arquivo, o único
 * que pode conter endereço de motor escrito por extenso — e o portão de
 * `config.test.ts` cobra isso varrendo `src/` inteiro por texto. A `guarda-da-galeria`
 * nasceu com a lista dentro dela (PR #714) e derrubou o portão para TODA proposta
 * aberta no repositório, inclusive as que não encostavam naquele arquivo.
 *
 * Isentar aquele arquivo no portão teria calado o alarme em vez de resolver o que
 * ele acusa — e a REGRA ZERO-C item 6 proíbe isso. Trazer a lista para cá resolve.
 */
export const HOSTS_DE_PRODUCAO = [
  'axiomgo-engine-production.up.railway.app',
  'api.axiomgo.io',
] as const

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '')
}

/**
 * True ONLY when the page is served by a developer's local Vite dev server.
 *
 * Why this is not just a hostname check: the packaged Android app (repo
 * `axiomgo-app`, Capacitor shell) ALSO runs on the hostname `localhost`. It
 * embeds this build under `webDir` and serves it from the origin
 * `https://localhost` — by design (`capacitor.config.json`, `androidScheme:
 * "https"`), and locked by a test in that repo because it is the origin Clerk
 * is configured to allow.
 *
 * Reading that origin as "developer machine" is the defect measured on the
 * Appetize emulator (Pixel 7 / Android 13): the APK was built with the staging
 * engine baked into `VITE_ENGINE_URL`, ignored it, and called
 * `http://localhost:4000` — a port nothing listens on inside a phone or a
 * cloud emulator. Symptom: ERR_CONNECTION_REFUSED.
 *
 * Two independent signals separate the two, and either one alone is enough:
 *  - the Capacitor bridge object exists only inside the packaged app;
 *  - the shell origin is always `https:`, a Vite dev server is plain `http:`.
 */
function isLocalDevServer(): boolean {
  if (typeof window === 'undefined') return false
  const { hostname, protocol } = window.location
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') return false
  if ('Capacitor' in window) return false
  return protocol !== 'https:'
}

/**
 * Derive the single-port WS endpoint from the engine's HTTP(S) base:
 * `https://host` → `wss://host/ws`, `http://host` → `ws://host/ws`. Used when
 * no explicit `VITE_ENGINE_WS_URL` is set — the WS URL then follows the same
 * (env-provided, never-hardcoded-prod) engine host as REST, instead of failing
 * the whole build. The `^http` → `ws` swap turns `https` into `wss` (the `s`
 * is preserved) and `http` into `ws`.
 */
function deriveWsUrl(engineHttpUrl: string): string {
  return `${stripTrailingSlash(engineHttpUrl).replace(/^http/, 'ws')}/ws`
}

function missingEnvError(varName: string): Error {
  return new Error(
    `[config] ${varName} is not set. Refusing to fall back to a hardcoded service URL in a ` +
      'production build (standard-core.md §9) — set it in the deploy environment (Vercel).',
  )
}

/**
 * REST/WS base for the engine, e.g. `https://axiomgo-engine-staging.up.railway.app`
 * (no trailing slash). Backed by `VITE_ENGINE_URL`.
 */
export function getEngineUrl(): string {
  const raw = import.meta.env.VITE_ENGINE_URL as string | undefined
  if (raw) return stripTrailingSlash(raw)
  if (import.meta.env.DEV) return DEV_ENGINE_URL
  throw missingEnvError('VITE_ENGINE_URL')
}

/**
 * WebSocket URL for the engine's single-port `/ws` endpoint. Prefers the
 * explicit `VITE_ENGINE_WS_URL`; when unset, DERIVES the WS URL from
 * `VITE_ENGINE_URL` (the same env-provided engine host used by REST) rather
 * than throwing — mirroring `getEngineRestUrl`. This is what keeps a deploy
 * that sets only `VITE_ENGINE_URL` (as the develop/staging Vercel envs do)
 * booting instead of white-screening on module load (`resilience.ts` calls
 * this at top level). It still NEVER falls back to a hardcoded production URL:
 * the derived host is always the configured engine host, and if neither var is
 * set in a production build it throws.
 */
export function getEngineWsUrl(): string {
  const raw = import.meta.env.VITE_ENGINE_WS_URL as string | undefined
  if (raw) return raw
  const engineHttpUrl = import.meta.env.VITE_ENGINE_URL as string | undefined
  if (engineHttpUrl) return deriveWsUrl(engineHttpUrl)
  if (import.meta.env.DEV) return DEV_ENGINE_WS_URL
  throw missingEnvError('VITE_ENGINE_WS_URL')
}

/**
 * REST base used by the SSE/upload clients. Prefers `VITE_ENGINE_REST_URL`;
 * when unset, falls back to `VITE_ENGINE_URL` (via `getEngineUrl`) — except
 * when the page is served by a local Vite dev server, where it points at the
 * local engine dev server instead. See `isLocalDevServer` for why that is not
 * a bare hostname check.
 */
export function getEngineRestUrl(): string {
  const explicit = import.meta.env.VITE_ENGINE_REST_URL as string | undefined
  if (explicit) return stripTrailingSlash(explicit)

  if (isLocalDevServer()) return LOCAL_ENGINE_REST_URL

  return getEngineUrl()
}

/**
 * WS URL used by `ConnectionManager`'s live transport. Prefers
 * `VITE_ENGINE_WS_URL`; when unset, falls back to the `VITE_ENGINE_URL`-derived
 * WS URL (via `getEngineWsUrl`) — except when the page is served by a local
 * Vite dev server, where it points at the local engine dev server instead. See
 * `isLocalDevServer` for why that is not a bare hostname check.
 */
export function getEngineWsConnectUrl(): string {
  const explicit = import.meta.env.VITE_ENGINE_WS_URL as string | undefined
  if (explicit) return explicit

  if (isLocalDevServer()) return LOCAL_ENGINE_WS_URL

  return getEngineWsUrl()
}
